import React, { useState, useMemo, useRef } from 'react';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { useData } from '../DataContext';
import { useTheme } from '../ThemeContext';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

interface ForecastPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
}

const ForecastPage: React.FC<ForecastPageProps> = ({ area, branch, startIdx, endIdx }) => {
  const { MONTHS, getRevenueExpensesSeries, profitLoss, profitLossByBranch, branches } = useData();
  const { chart: chartTheme } = useTheme();

  const [revGrowthType, setRevGrowthType] = useState<'percent' | 'amount'>('percent');
  const [revenueGrowth, setRevenueGrowth] = useState(0); // in %
  const [revenueGrowthAmount, setRevenueGrowthAmount] = useState(0); // in Rupiah
  const [revProjectionModel, setRevProjectionModel] = useState<'fix' | 'growth'>('growth');

  const [opexGrowthType, setOpexGrowthType] = useState<'percent' | 'amount'>('percent');
  const [opexGrowth, setOpexGrowth] = useState(0); // in %
  const [opexGrowthAmount, setOpexGrowthAmount] = useState(0); // in Rupiah
  const [opexProjectionModel, setOpexProjectionModel] = useState<'fix' | 'growth'>('growth');

  const chartRef = useRef<any>(null);
  const [copied, setCopied] = useState(false);

  const effectiveKey = branch && branch !== 'ALL' ? branch : area;
  const series = useMemo(
    () => getRevenueExpensesSeries(effectiveKey, Math.max(0, endIdx - 2), endIdx),
    [effectiveKey, endIdx]
  );

  const getCurrentBEP = useMemo(() => {
    if (branch && branch !== 'ALL' && profitLossByBranch[branch]) {
      const plData = profitLossByBranch[branch];
      const branchObj = branches.find(b => b.code === branch);
      if (!branchObj) return 0;

      let bep = 0;
      for (let i = 0; i <= endIdx; i++) {
        bep += plData.netProfit[i] || 0;
      }
      return bep;
    }
    return 0;
  }, [branch, endIdx, profitLossByBranch, branches]);

  const pl = useMemo(() => {
    return profitLossByBranch[effectiveKey] ?? (profitLoss as any)[effectiveKey] ?? profitLoss['ALL'];
  }, [effectiveKey, profitLossByBranch, profitLoss]);

  const baseAverages = useMemo(() => {
    if (!series || !series.revenue || series.revenue.length === 0 || !pl) {
      return { avgRevenue: 0, avgOpex: 0, avgCogs: 0, avgOther: 0 };
    }
    const lastThreeMonths = (series.revenue as any[]).slice(-3);
    const lastThreeOpex = (series.opex as any[]).slice(-3);
    const avgRevenue = lastThreeMonths.reduce((a, b) => a + b, 0) / 3;
    const avgOpex = lastThreeOpex.reduce((a, b) => a + b, 0) / 3;

    const lastThreeCogs = (pl.cogs.total).slice(-3);
    const lastThreeOther = (pl.otherIncomeExpense).slice(-3);
    const avgCogs = lastThreeCogs.reduce((a: number, b: number) => a + b, 0) / 3;
    const avgOther = lastThreeOther.reduce((a: number, b: number) => a + b, 0) / 3;

    return { avgRevenue, avgOpex, avgCogs, avgOther };
  }, [series, pl]);

  const forecast = useMemo(() => {
    if (!series || !series.revenue || series.revenue.length === 0 || !pl) return [];

    const { avgRevenue, avgOpex, avgCogs, avgOther } = baseAverages;
    const rows = [];
    let cumulativeBEP = getCurrentBEP;

    const currentMonthRevenue = series.revenue[series.revenue.length - 1];
    const currentMonthOpex = series.opex[series.opex.length - 1];
    const currentMonthProfit = pl.netProfit[endIdx] || 0;

    rows.push({
      month: MONTHS[endIdx]?.label || '',
      isActual: true,
      revenue: currentMonthRevenue,
      opex: currentMonthOpex,
      netProfit: currentMonthProfit,
      cumulativeBEP: cumulativeBEP,
      monthsToBEP: cumulativeBEP >= 0 ? 0 : (currentMonthProfit > 0 ? -cumulativeBEP / currentMonthProfit : -1),
    });

    for (let i = 1; i <= 6; i++) {
      let revenueForecast = 0;
      if (revGrowthType === 'percent') {
        if (revProjectionModel === 'fix') {
          revenueForecast = avgRevenue * (1 + (revenueGrowth / 100));
        } else {
          revenueForecast = avgRevenue * (1 + (revenueGrowth / 100) * i);
        }
      } else {
        if (revProjectionModel === 'fix') {
          revenueForecast = avgRevenue + revenueGrowthAmount;
        } else {
          revenueForecast = avgRevenue + (revenueGrowthAmount * i);
        }
      }

      let opexForecast = 0;
      if (opexGrowthType === 'percent') {
        if (opexProjectionModel === 'fix') {
          opexForecast = avgOpex * (1 + (opexGrowth / 100));
        } else {
          opexForecast = avgOpex * (1 + (opexGrowth / 100) * i);
        }
      } else {
        if (opexProjectionModel === 'fix') {
          opexForecast = avgOpex + opexGrowthAmount;
        } else {
          opexForecast = avgOpex + (opexGrowthAmount * i);
        }
      }

      // Net profit = Revenue - OpEx - COGS + Other Income/Expense
      const netProfitForecast = revenueForecast - opexForecast - avgCogs + avgOther;
      cumulativeBEP += netProfitForecast;

      rows.push({
        month: `+${i}M`,
        isActual: false,
        revenue: Math.max(0, revenueForecast),
        opex: Math.max(0, opexForecast),
        netProfit: netProfitForecast,
        cumulativeBEP: cumulativeBEP,
        monthsToBEP: cumulativeBEP >= 0 ? 0 : (netProfitForecast > 0 ? -cumulativeBEP / netProfitForecast : -1),
      });
    }

    return rows;
  }, [series, revGrowthType, revenueGrowth, revenueGrowthAmount, revProjectionModel, opexGrowthType, opexGrowth, opexGrowthAmount, opexProjectionModel, endIdx, getCurrentBEP, pl, baseAverages, MONTHS]);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = chartRef.current.canvas as HTMLCanvasElement;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const ctx = tempCanvas.getContext('2d');
      
      if (ctx) {
        ctx.fillStyle = '#ffffff'; 
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        
        tempCanvas.toBlob(async (blob) => {
          if (blob) {
            const item = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        }, 'image/png');
      }
    } catch (err) {
      console.error('Failed to copy chart image: ', err);
      alert('Fitur salin gambar tidak didukung di browser ini atau izin ditolak.');
    }
  };

  const chartData = useMemo(() => {
    const labels = forecast.map(row => row.month);
    const cumulativeBEPData = forecast.map(row => row.cumulativeBEP);

    return {
      labels,
      datasets: [
        {
          label: 'BEP Kumulatif',
          data: cumulativeBEPData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: true,
          pointRadius: 4,
          pointBackgroundColor: '#3b82f6',
          borderWidth: 3,
        }
      ]
    };
  }, [forecast]);

  return (
    <div className="data-table-card animate-in">
      <div className="data-table-header">
        <div>
          <div className="data-table-title">🔮 Forecast</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Proyeksi 6 bulan ke depan berdasarkan rata-rata 3 bulan terakhir
          </div>
        </div>
      </div>

      {/* KPI Cards & Averages Section */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', backgroundColor: 'var(--bg-secondary)' }}>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Avg Revenue (Last 3M)</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {formatRupiah(baseAverages.avgRevenue)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Avg OpEx (Last 3M)</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {formatRupiah(baseAverages.avgOpex)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>BEP Kumulatif</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: getCurrentBEP >= 0 ? '#10b981' : '#ef4444' }}>
            {formatRupiah(getCurrentBEP)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Status BEP</div>
          <span
            style={{
              fontSize: '11px',
              fontWeight: '700',
              padding: '4px 8px',
              borderRadius: '12px',
              backgroundColor: getCurrentBEP >= 0 ? '#d1fae5' : '#fef3c7',
              color: getCurrentBEP >= 0 ? '#065f46' : '#92400e',
              display: 'inline-block',
              marginTop: '2px'
            }}
          >
            {getCurrentBEP >= 0 ? '✓ Pass' : '⏳ Ongoing'}
          </span>
        </div>
      </div>

      {/* Control Box: Growth settings */}
      <div style={{ padding: '16px 20px', display: 'flex', gap: '32px', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
        {/* Revenue Growth controls */}
        <div style={{ flex: 1, minWidth: '280px', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-primary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>Revenue Growth</span>
            
            {/* Toggle group */}
            <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              <button
                type="button"
                onClick={() => setRevGrowthType('percent')}
                style={{
                  padding: '2px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: revGrowthType === 'percent' ? '#10b981' : 'var(--bg-secondary)',
                  color: revGrowthType === 'percent' ? '#fff' : 'var(--text-muted)'
                }}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => setRevGrowthType('amount')}
                style={{
                  padding: '2px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: revGrowthType === 'amount' ? '#10b981' : 'var(--bg-secondary)',
                  color: revGrowthType === 'amount' ? '#fff' : 'var(--text-muted)'
                }}
              >
                Rp
              </button>
            </div>
          </div>

          {revGrowthType === 'percent' ? (
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                Pertumbuhan Persentase: <strong>{revenueGrowth}%</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="range"
                  min="-20"
                  max="50"
                  step="1"
                  value={Math.max(-20, Math.min(50, revenueGrowth))}
                  onChange={e => setRevenueGrowth(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#10b981' }}
                />
                <input
                  type="number"
                  value={revenueGrowth}
                  onChange={e => {
                    const val = parseInt(e.target.value);
                    setRevenueGrowth(isNaN(val) ? 0 : val);
                  }}
                  style={{
                    width: '65px',
                    padding: '4px 6px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    outline: 'none',
                    fontWeight: '600',
                  }}
                />
                <span style={{ fontSize: '11px', fontWeight: '600', color: revenueGrowth >= 0 ? '#10b981' : '#ef4444', minWidth: '15px' }}>
                  %
                </span>
              </div>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                Pertumbuhan Nominal: <strong>{formatRupiah(revenueGrowthAmount)}</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="range"
                  min="-100000000"
                  max="200000000"
                  step="5000000"
                  value={Math.max(-100000000, Math.min(200000000, revenueGrowthAmount))}
                  onChange={e => setRevenueGrowthAmount(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#10b981' }}
                />
                <input
                  type="number"
                  value={parseFloat((revenueGrowthAmount / 1e6).toFixed(1))}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    setRevenueGrowthAmount(isNaN(val) ? 0 : val * 1e6);
                  }}
                  style={{
                    width: '65px',
                    padding: '4px 6px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    outline: 'none',
                    fontWeight: '600',
                  }}
                />
                <span style={{ fontSize: '11px', fontWeight: '600', color: revenueGrowthAmount >= 0 ? '#10b981' : '#ef4444', minWidth: '15px' }}>
                  Jt
                </span>
              </div>
            </div>
          )}

          {/* Model Proyeksi */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', borderTop: '1px dashed var(--border-light)', paddingTop: '8px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Model Proyeksi:</span>
            <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              <button
                type="button"
                onClick={() => setRevProjectionModel('fix')}
                style={{
                  padding: '3px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: revProjectionModel === 'fix' ? '#10b981' : 'var(--bg-secondary)',
                  color: revProjectionModel === 'fix' ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.15s'
                }}
              >
                Fix (Flat)
              </button>
              <button
                type="button"
                onClick={() => setRevProjectionModel('growth')}
                style={{
                  padding: '3px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: revProjectionModel === 'growth' ? '#10b981' : 'var(--bg-secondary)',
                  color: revProjectionModel === 'growth' ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.15s'
                }}
              >
                Growth (Bertambah)
              </button>
            </div>
          </div>
        </div>

        {/* OpEx Growth controls */}
        <div style={{ flex: 1, minWidth: '280px', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-primary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>OpEx Growth</span>
            
            {/* Toggle group */}
            <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              <button
                type="button"
                onClick={() => setOpexGrowthType('percent')}
                style={{
                  padding: '2px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: opexGrowthType === 'percent' ? '#ef4444' : 'var(--bg-secondary)',
                  color: opexGrowthType === 'percent' ? '#fff' : 'var(--text-muted)'
                }}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => setOpexGrowthType('amount')}
                style={{
                  padding: '2px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: opexGrowthType === 'amount' ? '#ef4444' : 'var(--bg-secondary)',
                  color: opexGrowthType === 'amount' ? '#fff' : 'var(--text-muted)'
                }}
              >
                Rp
              </button>
            </div>
          </div>

          {opexGrowthType === 'percent' ? (
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                Pertumbuhan Persentase: <strong>{opexGrowth}%</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="range"
                  min="-20"
                  max="50"
                  step="1"
                  value={Math.max(-20, Math.min(50, opexGrowth))}
                  onChange={e => setOpexGrowth(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#ef4444' }}
                />
                <input
                  type="number"
                  value={opexGrowth}
                  onChange={e => {
                    const val = parseInt(e.target.value);
                    setOpexGrowth(isNaN(val) ? 0 : val);
                  }}
                  style={{
                    width: '65px',
                    padding: '4px 6px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    outline: 'none',
                    fontWeight: '600',
                  }}
                />
                <span style={{ fontSize: '11px', fontWeight: '600', color: opexGrowth >= 0 ? '#ef4444' : '#10b981', minWidth: '15px' }}>
                  %
                </span>
              </div>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                Pertumbuhan Nominal: <strong>{formatRupiah(opexGrowthAmount)}</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="range"
                  min="-100000000"
                  max="200000000"
                  step="5000000"
                  value={Math.max(-100000000, Math.min(200000000, opexGrowthAmount))}
                  onChange={e => setOpexGrowthAmount(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#ef4444' }}
                />
                <input
                  type="number"
                  value={parseFloat((opexGrowthAmount / 1e6).toFixed(1))}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    setOpexGrowthAmount(isNaN(val) ? 0 : val * 1e6);
                  }}
                  style={{
                    width: '65px',
                    padding: '4px 6px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    outline: 'none',
                    fontWeight: '600',
                  }}
                />
                <span style={{ fontSize: '11px', fontWeight: '600', color: opexGrowthAmount >= 0 ? '#ef4444' : '#10b981', minWidth: '15px' }}>
                  Jt
                </span>
              </div>
            </div>
          )}

          {/* Model Proyeksi */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', borderTop: '1px dashed var(--border-light)', paddingTop: '8px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Model Proyeksi:</span>
            <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              <button
                type="button"
                onClick={() => setOpexProjectionModel('fix')}
                style={{
                  padding: '3px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: opexProjectionModel === 'fix' ? '#ef4444' : 'var(--bg-secondary)',
                  color: opexProjectionModel === 'fix' ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.15s'
                }}
              >
                Fix (Flat)
              </button>
              <button
                type="button"
                onClick={() => setOpexProjectionModel('growth')}
                style={{
                  padding: '3px 8px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                  backgroundColor: opexProjectionModel === 'growth' ? '#ef4444' : 'var(--bg-secondary)',
                  color: opexProjectionModel === 'growth' ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.15s'
                }}
              >
                Growth (Bertambah)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Forecast Data Table */}
      <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Bulan</th>
              <th style={{ textAlign: 'right', padding: '10px 8px' }}>Revenue</th>
              <th style={{ textAlign: 'right', padding: '10px 8px' }}>OpEx</th>
              <th style={{ textAlign: 'right', padding: '10px 8px' }}>Net Profit</th>
              <th style={{ textAlign: 'right', padding: '10px 8px' }}>BEP Kumulatif</th>
              <th style={{ textAlign: 'right', padding: '10px 8px' }}>Gap to BEP</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {forecast.map((row: any, i) => (
              <tr key={i} style={{ backgroundColor: row.isActual ? 'var(--bg-secondary)' : 'inherit', borderBottom: '1px solid var(--border-light)' }}>
                <td style={{ padding: '10px 8px', fontWeight: '600' }}>{row.month}</td>
                <td style={{ textAlign: 'right', padding: '10px 8px' }}>{formatRupiah(row.revenue)}</td>
                <td style={{ textAlign: 'right', padding: '10px 8px' }}>{formatRupiah(row.opex)}</td>
                <td style={{ textAlign: 'right', padding: '10px 8px', color: row.netProfit >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                  {formatRupiah(row.netProfit)}
                </td>
                <td style={{ textAlign: 'right', padding: '10px 8px', color: row.cumulativeBEP >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                  {formatRupiah(row.cumulativeBEP)}
                </td>
                <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '12px' }}>
                  {row.cumulativeBEP >= 0 ? (
                    <span style={{ color: '#10b981', fontWeight: '600' }}>—</span>
                  ) : (
                    <div style={{ color: '#f59e0b', fontWeight: '600' }}>
                      <div>{formatRupiah(-row.cumulativeBEP)}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', fontWeight: 'normal' }}>
                        {row.monthsToBEP > 0 ? `(${row.monthsToBEP.toFixed(1)} bln)` : '(N/A)'}
                      </div>
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'center', padding: '10px 8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  {row.isActual ? '✓ Actual' : '📊 Est'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Projection Chart Section */}
      <div style={{ padding: '20px', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            📊 Visualisasi Proyeksi 6 Bulan Ke Depan
          </h3>
          <button 
            onClick={handleCopyChart}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              fontSize: '11px',
              fontWeight: '600',
              borderRadius: '6px',
              border: `1px solid ${copied ? '#10b981' : 'var(--border-light)'}`,
              backgroundColor: copied ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)',
              color: copied ? '#10b981' : 'var(--text-primary)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {copied ? 'Tersalin!' : 'Salin Chart'}
          </button>
        </div>

        <div style={{ width: '100%', overflowX: 'auto', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid var(--border-light)', padding: '12px', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', height: '360px', minWidth: '100%' }}>
            <Line
              ref={chartRef}
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: true, labels: { color: chartTheme.legendColor, font: { size: 11, family: 'Inter' } } },
                  tooltip: {
                    backgroundColor: chartTheme.tooltipBg,
                    titleColor: chartTheme.tooltipTitle,
                    bodyColor: chartTheme.tooltipBody,
                    borderColor: chartTheme.tooltipBorder,
                    borderWidth: 1,
                  },
                  datalabels: {
                    anchor: 'end',
                    align: 'end',
                    offset: 4,
                    font: {
                      family: 'Inter',
                      size: 9,
                      weight: 'bold',
                    },
                    color: (context: any) => context.dataset.borderColor,
                    formatter: (value: any) => {
                      if (value === 0 || value === null) return '';
                      return `Rp ${(value / 1e6).toFixed(1)} Jt`;
                    },
                  } as any,
                },
                scales: {
                  x: {
                    ticks: { color: chartTheme.tickColor, font: { size: 10, family: 'Inter' } },
                    grid: { color: chartTheme.gridColorX },
                  },
                  y: {
                    ticks: {
                      color: chartTheme.tickColor,
                      font: { size: 10, family: 'Inter' },
                      callback: (value: any) => `Rp ${(value / 1e6).toFixed(0)} Jt`,
                    },
                    grid: { color: chartTheme.gridColorY },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 20px', backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-muted)' }}>
        <strong>Note:</strong> Forecast menggunakan linear growth formula berdasarkan rata-rata 3 bulan terakhir.
        Sesuaikan pilihan tipe pertumbuhan (% atau Rp) dan geser slider untuk mensimulasikan berbagai skenario proyeksi.
      </div>
    </div>
  );
};

export default ForecastPage;
